import React, { useState } from "react";

import { process, State } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridDataStateChangeEvent, GridRowClickEvent, GridToolbar } from "@progress/kendo-react-grid";
import { GridRowDoubleClickEvent } from "@progress/kendo-react-grid/dist/npm/interfaces/events";
import remove from "lodash/remove";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";

import { derender, render } from "../../../../../../state/actions/renderActions";
import { IDocument, IDossier, IFileData } from "../../../../../../utils/types/models";
import { newKey } from "../../../../../../utils/utils";
import Confirm from "../../../../../global/confirm";
import FileViewer from "../../../../../global/fileviewer";
import { getGridCommandColumn } from "../../../../../global/gridpanel";
import { customCell, dateCell } from "../../../../../global/gridpanel/customCells/gridCells";
import { IAnchorCommand, IFunctionCommand, IRecordCommand } from "../../../../../global/gridpanel/customCells/gridCells/commandCell";
import { IDossierSectionProps } from "../../editor";
import DocumentEditor from "./editor";

import style from "./documents.module.scss";

const Documents: React.FC<IDossierSectionProps> = (props: IDossierSectionProps) => {
	const { t } = useTranslation();
	const [gridState, setGridState] = useState<State>({ sort: [{ field: "lastUploadedDate", dir: "desc" }] });
	const [currentNewId, setCurrentNewId] = useState(-1);
	const [selectedFile, setSelectedFile] = useState<IFileData>();
	const dispatch: Dispatch = useDispatch();

	function showEditor(record?: IDocument): void {
		const key: string = newKey("Document_Editor");
		dispatch(
			render(key, DocumentEditor, {
				record,
				onClose: (document: IDocument) => {
					if (document) {
						const newDossier: IDossier = { ...props.dossier };
						if (document.id) {
							remove(newDossier.documents, { id: document.id });
						} else {
							document.id = currentNewId;
							setCurrentNewId(currentNewId - 1);
						}
						newDossier.documents.push(document);
						props.onChange(newDossier);
					}
					dispatch(derender(key));
				},
				readOnly: props.readOnly
			})
		);
	}

	const commands: (IFunctionCommand<IDocument> | IRecordCommand<IDocument> | IAnchorCommand<IDocument>)[] = [
		{
			tooltip: t("edit"),
			iconClass: "las la-pencil-alt",
			recordAction: (record: IDocument) => {
				showEditor(record);
			}
		},
		{
			tooltip: t("remove"),
			iconClass: "las la-times",
			idAction: (id: number) => {
				const confirmKey: string = newKey("confirm");
				dispatch(
					render(confirmKey, Confirm, {
						title: t("confirm_title", { action: t("remove").toLowerCase() }),
						children: t("confirm_content", { action: t("remove").toLowerCase() }),
						onDecline: () => dispatch(derender(confirmKey)),
						onConfirm: () => {
							dispatch(derender(confirmKey));
							const newDossier: IDossier = { ...props.dossier };
							remove(newDossier.documents, { id });
							props.onChange(newDossier);
						}
					})
				);
			}
		},
		{
			tooltip: t("dawnload"),
			iconClass: "las la-cloud-download-alt",
			getHref: (dataItem: IDocument) => "/api/files/" + dataItem.file.internalFilename + "/" + encodeURI(dataItem.file.filename) + "?download=true"
		}
	];

	return (
		<div className="h-100 d-flex flex-column k-form">
			<div className="row">
				<div className="col">
					<h2 className={style.accentTitle}>{t("documents")}</h2>
				</div>
				<div className="col" style={{ textAlign: "right" }}>
					<h3>
						{props.dossier?.dossierNumber} - {props.dossier?.customer?.name}
					</h3>
				</div>
			</div>
			<div className={"row " + style.grid}>
				<div className="col">
					<Grid
						data={process(props.dossier.documents, gridState)}
						{...gridState}
						sortable
						style={{ height: "300px" }}
						onDataStateChange={(event: GridDataStateChangeEvent) => setGridState(event.dataState)}
						onRowDoubleClick={(event: GridRowDoubleClickEvent) => showEditor(event.dataItem)}
						onRowClick={(event: GridRowClickEvent) => setSelectedFile(event.dataItem.file)}
					>
						{!props.readOnly && (
							<GridToolbar>
								<Button primary onClick={() => showEditor()}>
									{t("add")}
								</Button>
							</GridToolbar>
						)}
						<GridColumn field="lastUploadedDate" title={t("lastUploadedDate")} cell={customCell(dateCell())} />
						<GridColumn field="file.filename" title={t("name")} />
						<GridColumn field="subject" title={t("subject")} />
						{!props.readOnly && getGridCommandColumn(t("actions"), commands)}
					</Grid>
				</div>
			</div>
			<div className="row flex-grow-1">
				<div className="col d-flex justify-content-center align-items-center">{selectedFile && <FileViewer file={selectedFile} />}</div>
			</div>
		</div>
	);
};

export default Documents;
